
import React from 'react';
import { 
  LayoutDashboard, 
  FileSearch, 
  ClipboardList, 
  CloudLightning, 
  Map,
  Award
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";

const Product = () => {
  return (
    <section id="product" className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            The <span className="text-gradient">Wamy</span> Platform
          </h2>
          <p className="text-lg text-gray-600">
            An all-in-one solution for property claims underwriting with powerful AI-driven tools that analyze,
            monitor, and optimize your case workflow from intake to settlement.
          </p>
        </div>

        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 mb-8 h-8">
            <TabsTrigger value="dashboard" className="flex items-center gap-1.5 text-xs">
              <LayoutDashboard className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="claims" className="flex items-center gap-1.5 text-xs">
              <ClipboardList className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Claim Management</span>
            </TabsTrigger>
            <TabsTrigger value="policy" className="flex items-center gap-1.5 text-xs">
              <FileSearch className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Policy Analysis</span>
            </TabsTrigger>
            <TabsTrigger value="weather" className="flex items-center gap-1.5 text-xs">
              <CloudLightning className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Weather Analysis</span>
            </TabsTrigger>
            <TabsTrigger value="aerial" className="flex items-center gap-1.5 text-xs">
              <Map className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Aerial Imagery</span>
            </TabsTrigger>
            <TabsTrigger value="score" className="flex items-center gap-1.5 text-xs">
              <Award className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Claim Score</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Intuitive Dashboard</h3>
                <p className="text-gray-600 mb-6">
                  Get a comprehensive overview of all your cases with our intuitive dashboard. Monitor claim statuses, 
                  track financial metrics, and identify urgent cases at a glance.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Real-time case status monitoring</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Financial metrics and claim values</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Recent activity timeline</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Quick action buttons for common tasks</p>
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-white rounded-xl shadow-lg">
                <AspectRatio ratio={16/10} className="bg-gradient-to-br from-wamy-50 to-teal-50 rounded-lg overflow-hidden">
                  <div className="w-full h-full p-4 flex items-center justify-center">
                    <div className="w-full h-full relative bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
                      {/* Navigation bar with logo */}
                      <div className="absolute top-4 left-4 right-4 h-10 bg-gradient-to-r from-wamy-500 to-teal-500 rounded-md flex items-center px-3">
                        <div className="text-xs text-white font-semibold">Wamy Dashboard</div>
                        <div className="flex ml-auto space-x-2">
                          <div className="h-5 w-5 bg-white/20 rounded-sm"></div>
                          <div className="h-5 w-5 bg-white/20 rounded-sm"></div>
                        </div>
                      </div>
                      
                      {/* Dashboard metrics cards */}
                      <div className="mt-14 grid grid-cols-2 gap-3">
                        <div className="h-24 bg-wamy-100/50 rounded-md p-3">
                          <div className="text-xs text-wamy-700 font-medium mb-1">Total Active Claims</div>
                          <div className="text-lg font-semibold text-wamy-800">127</div>
                          <div className="text-xs text-wamy-600 mt-1">↑ 12% from last month</div>
                        </div>
                        <div className="h-24 bg-teal-100/50 rounded-md p-3">
                          <div className="text-xs text-teal-700 font-medium mb-1">Claims Value</div>
                          <div className="text-lg font-semibold text-teal-800">$1.2M</div>
                          <div className="text-xs text-teal-600 mt-1">↑ 8% from last month</div>
                        </div>
                      </div>

                      {/* Recent activity list */}
                      <div className="mt-3 h-32 bg-gray-100/80 rounded-md p-3">
                        <div className="flex justify-between mb-2">
                          <div className="text-xs font-medium text-gray-700">Recent Activity</div>
                          <div className="text-xs text-wamy-600">View All</div>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <div className="h-5 w-full bg-white rounded flex items-center">
                            <div className="h-4 w-4 rounded-full bg-wamy-300 ml-2"></div>
                            <div className="text-xs text-gray-700 ml-2">Claim #8294 updated</div>
                            <div className="text-xs text-gray-500 ml-auto mr-2">2m ago</div>
                          </div>
                          <div className="h-5 w-full bg-white rounded flex items-center">
                            <div className="h-4 w-4 rounded-full bg-teal-300 ml-2"></div>
                            <div className="text-xs text-gray-700 ml-2">New claim added</div>
                            <div className="text-xs text-gray-500 ml-auto mr-2">1h ago</div>
                          </div>
                          <div className="h-5 w-full bg-white rounded flex items-center">
                            <div className="h-4 w-4 rounded-full bg-wamy-300 ml-2"></div>
                            <div className="text-xs text-gray-700 ml-2">Document uploaded</div>
                            <div className="text-xs text-gray-500 ml-auto mr-2">2h ago</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AspectRatio>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="claims" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Claim Management</h3>
                <p className="text-gray-600 mb-6">
                  Manage, organize, and track all your property claims in one central hub. Our intuitive system provides 
                  real-time updates, document management, and activity tracking for every case.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Comprehensive claim tracking</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Secure document management</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Automated status updates</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Multi-platform integration</p>
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-white rounded-xl shadow-lg">
                <AspectRatio ratio={16/10} className="bg-gradient-to-br from-wamy-50 to-blue-50 rounded-lg overflow-hidden">
                  <div className="w-full h-full p-4 flex items-center justify-center">
                    <div className="w-full h-full relative bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
                      {/* Header with search */}
                      <div className="flex items-center mb-5 justify-between">
                        <div className="text-sm font-medium text-wamy-700">Active Claims</div>
                        <div className="h-8 w-36 bg-gray-100 rounded-lg flex items-center px-2">
                          <div className="text-xs text-gray-400">Search claims...</div>
                        </div>
                      </div>
                      
                      {/* Claims list */}
                      <div className="space-y-3 mb-3">
                        <div className="h-auto min-h-14 border border-gray-200 rounded-lg p-2 flex">
                          <div className="h-10 w-10 bg-wamy-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <ClipboardList className="h-5 w-5 text-wamy-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-800">Claim #8294 - Roof Damage</div>
                            <div className="text-xs text-gray-500">Johnson Property • Filed: 06/15/2023</div>
                            <div className="mt-1 flex items-center">
                              <div className="h-2 w-2 rounded-full bg-yellow-500 mr-1"></div>
                              <div className="text-xs text-yellow-600">In Progress</div>
                            </div>
                          </div>
                          <div className="text-xs text-wamy-600 font-medium self-center flex-shrink-0 ml-2">View</div>
                        </div>
                        
                        <div className="h-auto min-h-14 border border-gray-200 rounded-lg p-2 flex">
                          <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <ClipboardList className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-800">Claim #7651 - Water Damage</div>
                            <div className="text-xs text-gray-500">Smith Residence • Filed: 05/22/2023</div>
                            <div className="mt-1 flex items-center">
                              <div className="h-2 w-2 rounded-full bg-green-500 mr-1"></div>
                              <div className="text-xs text-green-600">Settlement Proposed</div>
                            </div>
                          </div>
                          <div className="text-xs text-wamy-600 font-medium self-center flex-shrink-0 ml-2">View</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AspectRatio>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="policy" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Intelligent Policy Analysis</h3>
                <p className="text-gray-600 mb-6">
                  Wamy's AI automatically extracts and interprets key information from insurance policies, 
                  flagging important coverage details, exclusions, and depreciation rules.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Automatic policy information extraction</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Coverage analysis and limitation detection</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Depreciation rule interpretation</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Policy-specific recommendation engine</p>
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-white rounded-xl shadow-lg">
                <AspectRatio ratio={16/10} className="bg-gradient-to-br from-teal-50 to-gray-50 rounded-lg overflow-hidden">
                  <div className="w-full h-full p-4 flex items-center justify-center">
                    <div className="w-full h-full relative bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm flex">
                      {/* Document nav */}
                      <div className="w-1/3 border-r border-gray-200 pr-3">
                        <div className="text-xs font-medium text-gray-700 mb-4">Policy Documents</div>
                        <div className="space-y-2">
                          <div className="h-6 w-full bg-wamy-100 rounded-md flex items-center px-2">
                            <span className="text-xs text-wamy-700">Homeowner Policy</span>
                          </div>
                          <div className="h-6 w-full bg-gray-100 rounded-md flex items-center px-2">
                            <span className="text-xs text-gray-600">Declarations Page</span>
                          </div>
                          <div className="h-6 w-full bg-gray-100 rounded-md flex items-center px-2">
                            <span className="text-xs text-gray-600">Endorsements</span>
                          </div>
                          <div className="h-6 w-full bg-gray-100 rounded-md flex items-center px-2">
                            <span className="text-xs text-gray-600">Coverage Schedule</span>
                          </div>
                          <div className="h-6 w-full bg-gray-100 rounded-md flex items-center px-2">
                            <span className="text-xs text-gray-600">Exclusions</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Content area */}
                      <div className="w-2/3 pl-3">
                        <div className="flex justify-between mb-3">
                          <div className="text-sm font-medium text-wamy-700">Coverage Analysis</div>
                          <div className="h-6 w-8 bg-teal-200 rounded-full flex items-center justify-center">
                            <span className="text-xs text-teal-700">AI</span>
                          </div>
                        </div>
                        <div className="h-2 w-full bg-gray-100 rounded mb-6"></div>
                        
                        {/* Analysis findings */}
                        <div className="space-y-4">
                          <div className="p-2 bg-yellow-50 border border-yellow-200 rounded">
                            <div className="text-xs text-yellow-800">Section II - Coverage B has depreciation limitations for roof damage.</div>
                            <div className="text-xs text-yellow-600 mt-1">Page 12, Line 7-9</div>
                          </div>
                          <div className="p-2 bg-wamy-50 border border-wamy-200 rounded">
                            <div className="text-xs text-wamy-800">Wind damage coverage limited to $25,000 per occurrence.</div>
                            <div className="text-xs text-wamy-600 mt-1">Page 4, Line 13-15</div>
                          </div>
                          <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                            <div className="text-xs text-gray-800">Replacement cost coverage applies only if repairs completed within 180 days.</div>
                            <div className="text-xs text-gray-600 mt-1">Page 8, Line 22-26</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AspectRatio>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="weather" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Weather Event Analysis</h3>
                <p className="text-gray-600 mb-6">
                  Access detailed historical weather data for any property location. Wamy provides comprehensive 
                  reports on hurricanes, storms, floods, and other weather events that may impact your case.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Historical weather event tracking</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Wind speed and precipitation analysis</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Damage risk assessment reports</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Expert-ready documentation for court</p>
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-white rounded-xl shadow-lg">
                <AspectRatio ratio={16/10} className="bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg overflow-hidden">
                  <div className="w-full h-full p-4 flex items-center justify-center">
                    <div className="w-full h-full relative bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
                      {/* Header */}
                      <div className="mb-4 flex justify-between items-center">
                        <div className="text-sm font-medium text-blue-700">Hurricane Ian Analysis</div>
                        <div className="flex space-x-2">
                          <div className="h-6 w-6 bg-gray-200 rounded flex items-center justify-center">
                            <span className="text-xs text-gray-600">↓</span>
                          </div>
                          <div className="h-6 w-6 bg-blue-200 rounded flex items-center justify-center">
                            <span className="text-xs text-blue-600">⋮</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Event summary */}
                      <div className="rounded-lg bg-blue-50 p-3 mb-4">
                        <div className="flex justify-between mb-2">
                          <div className="flex items-center">
                            <div className="h-8 w-8 bg-blue-300 rounded-full mr-2 flex items-center justify-center">
                              <CloudLightning className="h-4 w-4 text-blue-700" />
                            </div>
                            <div className="text-xs font-medium text-blue-700">Category 4 Hurricane</div>
                          </div>
                          <div className="text-xs text-blue-600">Sep 28, 2022</div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-sm font-bold text-blue-800">155 mph</div>
                          <div className="text-xs text-blue-600">Peak wind at property location</div>
                        </div>
                      </div>
                      
                      {/* Data charts */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="h-20 bg-gray-50 rounded p-2">
                          <div className="text-xs text-gray-600 mb-2">Wind Speed (72hrs)</div>
                          <div className="h-8 w-full bg-gray-100 rounded flex items-end pl-2">
                            <div className="h-6 w-1/3 bg-blue-300 rounded"></div>
                            <div className="h-4 w-1/3 bg-blue-200 rounded ml-2"></div>
                            <div className="h-2 w-1/3 bg-blue-100 rounded ml-2"></div>
                          </div>
                        </div>
                        <div className="h-20 bg-gray-50 rounded p-2">
                          <div className="text-xs text-gray-600 mb-2">Rainfall (in)</div>
                          <div className="h-12 w-full bg-gradient-to-r from-blue-100 to-blue-300 rounded"></div>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div className="bg-gray-50 rounded-lg h-24 p-3">
                        <div className="text-xs text-gray-600 mb-3">Impact Timeline</div>
                        <div className="flex space-x-2">
                          <div className="h-10 w-1/4 bg-blue-100 rounded-md flex items-center justify-center">
                            <span className="text-xs text-blue-700">9/27<br/>6am</span>
                          </div>
                          <div className="h-10 w-1/4 bg-blue-200 rounded-md flex items-center justify-center">
                            <span className="text-xs text-blue-700">9/27<br/>6pm</span>
                          </div>
                          <div className="h-10 w-1/4 bg-blue-300 rounded-md flex items-center justify-center">
                            <span className="text-xs text-blue-700">9/28<br/>6am</span>
                          </div>
                          <div className="h-10 w-1/4 bg-blue-400 rounded-md flex items-center justify-center">
                            <span className="text-xs text-white">9/28<br/>6pm</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AspectRatio>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="aerial" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Aerial Imagery Analysis</h3>
                <p className="text-gray-600 mb-6">
                  Access historical and current aerial imagery of properties to identify changes over time 
                  and provide visual evidence to support your cases.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Historical property imagery timeline</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Pre and post-event comparison tools</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">AI-powered damage detection</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Exportable visual evidence for litigation</p>
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-white rounded-xl shadow-lg">
                <AspectRatio ratio={16/10} className="bg-gradient-to-br from-green-50 to-gray-50 rounded-lg overflow-hidden">
                  <div className="w-full h-full p-4 flex items-center justify-center">
                    <div className="w-full h-full relative bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
                      {/* View toggles */}
                      <div className="flex space-x-4 mb-3">
                        <div className="h-6 w-20 bg-green-200 rounded flex items-center justify-center">
                          <span className="text-xs text-green-700">Pre-Event</span>
                        </div>
                        <div className="h-6 w-20 bg-gray-200 rounded flex items-center justify-center">
                          <span className="text-xs text-gray-700">Post-Event</span>
                        </div>
                        <div className="h-6 w-20 bg-gray-200 rounded flex items-center justify-center">
                          <span className="text-xs text-gray-700">Compare</span>
                        </div>
                      </div>
                      
                      {/* Images grid */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="aspect-square bg-green-800/20 rounded-lg relative overflow-hidden">
                          <div className="absolute inset-0 grid grid-cols-6 grid-rows-6">
                            {Array.from({ length: 36 }).map((_, i) => (
                              <div 
                                key={i} 
                                className="border border-green-700/20"
                                style={{
                                  backgroundColor: `rgba(${Math.floor(Math.random() * 100 + 50)}, ${Math.floor(Math.random() * 100 + 150)}, ${Math.floor(Math.random() * 80 + 50)}, 0.6)`
                                }}
                              ></div>
                            ))}
                          </div>
                          <div className="absolute top-2 left-2 bg-black/40 text-white text-xs px-2 py-1 rounded">
                            June 10, 2022
                          </div>
                          <div className="absolute bottom-2 right-2 flex items-center justify-center rounded-full h-7 w-7 bg-white/70">
                            <div className="h-4 w-4 bg-green-500 rounded-sm"></div>
                          </div>
                        </div>
                        <div className="aspect-square bg-purple-800/20 rounded-lg relative overflow-hidden">
                          <div className="absolute inset-0 grid grid-cols-6 grid-rows-6">
                            {Array.from({ length: 36 }).map((_, i) => (
                              <div 
                                key={i} 
                                className="border border-purple-700/20"
                                style={{
                                  backgroundColor: `rgba(${Math.floor(Math.random() * 100 + 50)}, ${Math.floor(Math.random() * 80 + 50)}, ${Math.floor(Math.random() * 150 + 100)}, 0.6)`
                                }}
                              ></div>
                            ))}
                          </div>
                          <div className="absolute top-2 left-2 bg-black/40 text-white text-xs px-2 py-1 rounded">
                            June 12, 2022
                          </div>
                          <div className="absolute bottom-2 right-2 flex items-center justify-center rounded-full h-7 w-7 bg-white/70">
                            <div className="h-4 w-4 bg-purple-500 rounded-sm"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AspectRatio>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="score" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Claim Score & Analytics</h3>
                <p className="text-gray-600 mb-6">
                  Our proprietary scoring system analyzes claim details, policy coverage, and evidence to 
                  provide a comprehensive evaluation of case strength and potential settlement value.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Proprietary claim scoring algorithm</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Settlement value estimation</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Case strength assessment</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-wamy-100 flex items-center justify-center mr-3">
                      <span className="text-wamy-600 text-sm font-semibold">✓</span>
                    </div>
                    <p className="text-gray-700">Improvement recommendations</p>
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-white rounded-xl shadow-lg">
                <AspectRatio ratio={16/10} className="bg-gradient-to-br from-purple-50 to-gray-50 rounded-lg overflow-hidden">
                  <div className="w-full h-full p-4 flex items-center justify-center">
                    <div className="w-full h-full relative bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
                      {/* Score header */}
                      <div className="flex items-center mb-4">
                        <div className="mr-4">
                          <div className="text-sm font-medium text-gray-700">Claim #8294 Score</div>
                          <div className="text-xs text-gray-500">Johnson Property - Roof Damage</div>
                        </div>
                        <div className="ml-auto">
                          <div className="h-10 w-10 rounded-full bg-purple-500 flex items-center justify-center">
                            <span className="text-white font-bold">87</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Score breakdown */}
                      <div className="space-y-3 mb-4">
                        <div>
                          <div className="flex justify-between mb-1">
                            <div className="text-xs text-gray-600">Evidence Strength</div>
                            <div className="text-xs font-medium text-purple-700">92/100</div>
                          </div>
                          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 rounded-full" style={{ width: '92%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <div className="text-xs text-gray-600">Policy Coverage</div>
                            <div className="text-xs font-medium text-purple-700">85/100</div>
                          </div>
                          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 rounded-full" style={{ width: '85%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <div className="text-xs text-gray-600">Documentation</div>
                            <div className="text-xs font-medium text-purple-700">78/100</div>
                          </div>
                          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 rounded-full" style={{ width: '78%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <div className="text-xs text-gray-600">Precedent Alignment</div>
                            <div className="text-xs font-medium text-purple-700">90/100</div>
                          </div>
                          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 rounded-full" style={{ width: '90%' }}></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Recommendation */}
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="text-xs font-medium text-purple-800 mb-1">AI Recommendation</div>
                        <div className="text-xs text-purple-700">
                          Strong case with high settlement potential. Recommend obtaining additional weather documentation to strengthen claim further.
                        </div>
                        <div className="mt-2 text-xs font-medium text-purple-900">
                          Estimated Settlement: $35,000 - $42,000
                        </div>
                      </div>
                    </div>
                  </div>
                </AspectRatio>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Product;
